<!-- @format -->

<template>
	<div
		id="cs-screen"
		class="cs-screen"
	>
		<div
			class="cs-components-container"
			data-datepicker-scroller="true"
		>
			<div
				class="cs-component"
				:class="`${component.componentName}-container`"
				:id="`${component.componentName}-${index}`"
				v-for="(component, index) in screen.components"
				:key="component.id"
			>
				<cs-loadinglogo v-if="!componentLoadComplete" />
				<transition
					name="cs-fade"
					mode="out-in"
				>
					<component
						:is="dynamicComponent(component.componentName)"
						:index="index"
						:component-settings="component.componentSettings"
						:id="component.componentSettingsTemplateId"
						:title="component.translatedName || component.name"
						:tooltip="component.tooltip"
						:user="user"
						:theme="theme"
						:entities="entities"
						:header-logo="headerLogo"
						:key="getComponentKey(component)"
						:component-data="component"
						:status="component.status"
						:root-level-help-text="rootLevelHelpText"
						@showNotification="showNotification"
						@updateTheme="onUpdateTheme"
						@componentLoaded="setDynamicComponentState"
						@componentDestroyed="setDynamicComponentState"
						@open-fullscreen-wizard="openFullscreenWizard"
					/>
				</transition>
			</div>
		</div>
		<div class="cs-overlay-slider-wrapper">
			<cs-overlay-slider-keyframe
				v-if="overlayVisible"
				class="cs-bc-light cs-bd-thin-right cs-screen-form"
				direction="negative-left"
				:size-from-div-selector="overlaySliderTarget"
				:style-object-override="customSliderStyles"
			>
				<cs-loadinglogo v-if="!formLoadingComplete" />

				<cs-form
					v-if="decoratedSiteConfigForm != undefined"
					v-show="formLoadingComplete"
					header-arrow-direction="right"
					:config-mode="true"
					:settings="decoratedSiteConfigForm"
					@toggleModal="toggleOverlay"
					@saveConfigData="onSaveConfigData"
					@deleteConfigData="onDeleteConfigData"
					@childLoaded="onOverlayFormLoaded"
					@destroyingForm="onDestroyingForm"
					@showNotification="showNotification"
				/>
			</cs-overlay-slider-keyframe>

			<cs-overlay-slider-keyframe
				v-if="fullScreenWizardVisible"
				direction="negative-left"
				:class="'cs-fullscreen-wizard-slider'"
				:size-from-div-selector="'.cs-app'"
				:style-object-override="{position: 'fixed', width: '100%', height: '100%', zIndex: '100'}"
			>
				<component
					:is="fullScreenWizardComponent"
					:component-data="fullScreenWizardComponentData"
					@close="onCloseFullScreenWizard"
					@showNotification="showNotification"
				/>
			</cs-overlay-slider-keyframe>
		</div>
	</div>
</template>
<script type="text/javascript">
/** @format */

import MiComponentBase from '@/js/mixins/mi-componentBase';
import MiDynamicComponent from '@/js/mixins/mi-dynamicComponent';
import MiDynamicComponentLoadingUtils from '@/js/mixins/mi-dynamicComponentLoadingUtils';
import MiFormUtils from '@/js/mixins/mi-formUtils';
import MiNotificationEmitter from '@/js/mixins/mi-notificationEmitter';
import MiConfigUtils from '@/js/mixins/mi-configUtils.js';

import {EventComponentBus} from '@/js/events/event-componentbus';

import $csForm from '@/js/modules/csForm'; //new ES6 module that holds model data for currentFormValues & formSettings

/**
 * Generic component for displaying a 'screen' worth of components
 * Used for almost all routes, and passed a set of components to loop through and display
 * @version 1.0.0
 * @since 1.0.0
 */
export default {
	name: 'cs-screen',
	mixins: [MiComponentBase, MiDynamicComponent, MiDynamicComponentLoadingUtils, MiFormUtils, MiNotificationEmitter, MiConfigUtils],

	props: {
		/**
		 * The current screen object
		 */
		screen: {
			type: Object,
			required: true
		},

		/**
		 * The navigation object, included here only to keep it out of the DOM
		 */
		navigation: {
			type: Array,
			required: false,
			default: () => []
		},

		headerLogo: {
			type: String,
			required: false,
			default: () => ''
		},

		inPageComponentEditVersion: {
			type: Number,
			required: false,
			default: 0
		}
	},

	/**
	 * @private
	 * @ignore
	 */
	components: {
		'cs-loadinglogo': () => import(/* webpackChunkName: "cs-loadinglogo" */ '@/js/global/cs-loadinglogo'),
		'cs-notfound': () => import(/* webpackChunkName: "cs-notfound" */ '@/js/components/cs-notfound'),
		'cs-home': () => import(/* webpackChunkName: "cs-home" */ '@/js/components/cs-home/cs-home'),
		'cs-form': () => import(/* webpackChunkName: "cs-form" */ '@/js/components/cs-form/cs-form'),
		'cs-grid': () => import(/* webpackChunkName: "cs-grid" */ '@/js/components/cs-grid/cs-grid'),
		'cs-my-corestream': () => import(/* webpackChunkName: "cs-grid-dashboard" */ '@/js/components/cs-my-corestream/cs-my-corestream'),
		'cs-treeview': () => import(/* webpackChunkName: "cs-treeview" */ '@/js/components/cs-treeview'),
		'cs-gantt': () => import(/* webpackChunkName: "cs-gantt" */ '@/js/components/cs-gantt/cs-gantt'),
		'cs-reports': () => import(/* webpackChunkName: "cs-reports" */ '@/js/components/cs-reports/cs-reports'),
		'cs-config': () => import(/* webpackChunkName: "cs-config" */ '@/js/components/cs-config/cs-config'),
		'cs-accordion': () => import(/* webpackChunkName: "cs-accordion" */ '@/js/components/cs-accordion'),
		'cs-admin': () => import(/* webpackChunkName: "cs-admin" */ '@/js/components/cs-admin/cs-admin'),
		'cs-dataimport': () => import(/* webpackChunkName: "cs-dataimport" */ '@/js/components/cs-dataimport/cs-dataimport'),
		'cs-userprofile': () => import(/* webpackChunkName: "cs-userprofile" */ '@/js/components/cs-userprofile'),
		'cs-policy-manager': () => import(/* webpackChunkName: "cs-policy-manager" */ '@/js/components/cs-policy-manager'),
		'cs-policy-manager-v2-base': () => import(/* webpackChunkName: "cs-policy-manager-v2-base" */ '@/js/components/cs-policy-manager-v2/cs-policy-manager-v2-base'),
		'cs-static-translation-manager': () => import(/* webpackChunkName: "cs-static-translation-manager" */ '@/js/components/cs-static-translation-manager'),
		'cs-master-grid': () => import(/* webpackChunkName: "cs-master-grid" */ '@/js/components/cs-master-grid'),
		'cs-site-builder': () => import(/* webpackChunkName: "cs-site-builder" */ '@/js/components/cs-site-builder/cs-site-builder'),
		'cs-fct-collection-builder-import': () => import(/* webpackChunkName: "cs-fct-collection-builder-import" */ '@/js/components/cs-fct-collection-builder-import/cs-fct-collection-builder-import'),
		'cs-overlay-slider-keyframe': () => import(/* webpackChunkName: "cs-overlay-slider" */ '@/js/components/shared/cs-overlay-slider-keyframe'),
		'cs-config-import-export': () => import(/* webpackChunkName: "cs-config-import-export" */ '@/js/components/cs-config-import-export/cs-config-import-export'),
		'cs-data-stream': () => import(/* webpackChunkName: "cs-data-stream" */ '@/js/components/cs-data-stream/cs-data-stream')
	},

	i18n: {},

	data() {
		return {
			componentLoadComplete: false,
			decoratedSiteConfigForm: undefined, // Object type {},
			overlayVisible: false,
			formLoadingComplete: false,
			overlaySliderTarget: '',
			customSliderStyles: {},
			siteBuilderVisible: false,
			fullScreenWizardVisible: false,
			fullScreenWizardComponent: null,
			fullScreenWizardComponentData: {}
		};
	},

	mounted() {
		EventComponentBus.$on('csUserClearPerosnalisedSettings', (componentSettings = undefined) => {
			this.onClearPersonalisedSettings(componentSettings);
		});

		EventComponentBus.$on('csToggleComponentSettingsOverlay', () => {
			this.onEditComponentSettings();
		});

		// We're clearing app loading overlay when screen component finished loading
		this.$hideAppLoadingOverlay();
	},

	destroyed() {
		EventComponentBus.$off('csUserClearPerosnalisedSettings');
		EventComponentBus.$off('csToggleComponentSettingsOverlay');
	},

	methods: {
		/**
		 * @public
		 * @description
		 * Make sure when a form is destroyed we reset the componentSettingsForm property
		 * @param {String} itemId a guid of the component id used for the form
		 */
		onDestroyingForm(itemId) {
			if (this.componentSettingsForm != undefined && this.componentSettingsForm.itemId === itemId) {
				this.componentSettingsForm = undefined;
			}
		},
		/**
		 * @public
		 * @description
		 * Sets / Toggles the status of the current form
		 *
		 * @param { Boolean } loaded Whether the child component is loaded
		 */
		onOverlayFormLoaded(loaded) {
			setTimeout(() => {
				if (this.$_.isBoolean(loaded)) {
					this.formLoadingComplete = loaded;
				} else {
					this.formLoadingComplete = !this.formLoadingComplete;
				}
			}, 600);
		},

		/**
		 * @public
		 * @description
		 * Generates unique key for the compoment
		 *
		 * @returns { String } key
		 */
		getComponentKey(component) {
			this.$cs_.timerStart('getComponentKey');

			let componentKey = component.id ? `cs-component-${component.componentName}-${component.id}-${this.inPageComponentEditVersion}` : `cs-component-${this.$_.uniqueId()}-${this.inPageComponentEditVersion}`;
			this.$cs_.timerAdd('getComponentKey');
			return componentKey;
		},

		/**
		 * @public
		 * @description
		 * Sets a loading state of dynamic component
		 *
		 * @returns void
		 */
		setDynamicComponentState(event) {
			this.componentLoadComplete = event;
		},

		/**
		 * @public
		 * @description
		 * Toggles overlay visibility
		 * @param {Boolean} isState
		 *
		 * @returns void
		 */
		toggleOverlay(isState) {
			this.overlayVisible = isState != undefined ? isState : !this.overlayVisible;

			if (this.overlayVisible === false) {
				this.formLoadingComplete = false;
				this.decoratedSiteConfigForm = undefined;
			}
		},

		/**
		 * @public
		 * @description
		 * Passes on an emission to update the theme.
		 *
		 * @param {Object} newThemeSettings
		 */
		onUpdateTheme(newThemeSettings) {
			/**
			 * @description
			 * emits a request to update the theme if the entity it is for is active
			 *
			 * @event updateTheme
			 * @type {Object}
			 */
			this.$emit('updateTheme', newThemeSettings);
		},

		/**
		 * @public
		 * @description
		 * Clears user personal settings for current component
		 *
		 * @param { Object } componentSettings used to override our default of the first screen component eg from a my corestream page grid
		 * @returns void
		 */
		onClearPersonalisedSettings(currentComponentSettings = undefined) {
			try {
				let componentSettingsTemplateId;
				if (currentComponentSettings != undefined) {
					componentSettingsTemplateId = currentComponentSettings.id;
				} else {
					const currentComponent = this.screen.components[0];
					componentSettingsTemplateId = currentComponent.componentSettingsTemplateId;
				}
				const dataUrl = `${this.$getApiBaseUrl()}/api/GenericObjectController/DeleteUserComponentTemplate/${componentSettingsTemplateId}/`;

				this.$axios
					.get(dataUrl)
					.then((response) => {
						this.showNotification('success', this.$translateWithDefault('personalSettings.clearedSuccess'), true, 'standard');

						setTimeout(() => {
							location.reload();
						}, 800);
						// Keep this for V2 where we can continue and implement a 'non refresh approach'
						// const customColumnSelectorInGridComponent = this.$cs_.querySelectorVue(this, {name: 'cs-grid-custom-col-selector', dataKey: 'gridComponentSettingsId', dataValue: componentSettingsTemplateId});
						// if (customColumnSelectorInGridComponent != undefined) {
						// 	customColumnSelectorInGridComponent.onClearPersonalisedSettings(); //implement this method in the component
						// }
					})
					.catch((error) => {
						this.$recordError({
							type: 'error',
							error: error,
							displayToUser: true,
							errorCode: '000219'
						});
					});
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000220'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Configures current componentSettings form
		 * Toggles overlay visibility
		 *
		 * @retuns void
		 */
		async onEditComponentSettings() {
			try {
				//set the overlay slider style object
				if (this.screen.name === 'Home') {
					this.overlaySliderTarget = '.cs-home--body';
					this.customSliderStyles = {position: 'fixed', width: '900px', maxWidth: '100%', top: '0px', height: '100%'};
				} else {
					this.overlaySliderTarget = '.cs-components-container';
					this.customSliderStyles = {position: 'fixed', width: '900px', maxWidth: '100%'};
				}
				//activate the slider overlay
				this.toggleOverlay(true);

				//request a configForm object only once, this.configForms must not be mutated!
				if (this.configForms == undefined) {
					let configModule = await import('@/js/modules/configFormsES6');
					let requests = [configModule.default.getConfigFormsObject(), configModule.default.getConfigLookupsObject()];

					this.configForms = await requests[0];
					await requests[1]; //CONFIG LOOKUPS HAVE BEEN MOVED TO THE ES6 Module
				}

				await this.miConfigUtilsGetSiteConfig();

				let configForm = undefined,
					currentComponent = undefined;
				if ($csForm.getActiveFormCount() > 0) {
					// We are going to open the edit component settings for a site form
					const formData = await this.getCurrentComponentAndConfigForSiteFormFieldTemplates();
					configForm = formData.configForm;
					currentComponent = formData.currentComponent;
				} else {
					const formData = await this.getCurrentComponentAndConfigForComponentSettingsTemplates();
					configForm = formData.configForm;
					currentComponent = formData.currentComponent;
				}
				if (configForm != undefined) {
					const decoratedSiteConfigForm = {
						title: configForm.title == undefined || configForm.title === '' ? 'Form' : configForm.title,
						mode: 'edit',
						id: configForm.id,
						itemId: currentComponent.id,
						collectionId: configForm.collectionId,
						forceResponseData: true,
						configForm: configForm
					};

					//remove the clone button from config forms created from cs-screen
					if (Array.isArray(decoratedSiteConfigForm.configForm.actionButtons)) {
						let buttonIndex = decoratedSiteConfigForm.configForm.actionButtons.findIndex((v) => v.id === 'clone');
						if (buttonIndex !== -1) {
							decoratedSiteConfigForm.configForm.actionButtons.splice(buttonIndex, 1);
						}
						buttonIndex = decoratedSiteConfigForm.configForm.actionButtons.findIndex((v) => v.id === 'delete');
						if (buttonIndex !== -1) {
							decoratedSiteConfigForm.configForm.actionButtons.splice(buttonIndex, 1);
						}
					}

					this.decoratedSiteConfigForm = decoratedSiteConfigForm;
				}
			} catch (error) {
				this.toggleOverlay(false);
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000191'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Get the active component settings template and return a config form
		 * decorated by the fields it contains.
		 *
		 * @return {Object}
		 */
		async getCurrentComponentAndConfigForComponentSettingsTemplates() {
			try {
				//set the current component to the screen[0], modify the id and name and throw an error if we cannot find a match in this.config.
				let currentComponent = this.$cs_.deepClone(this.screen.components[0]);
				currentComponent.id = currentComponent.componentSettingsTemplateId;
				let response = await this.miConfigUtilsGetConfigItem('ComponentSettingTemplates', currentComponent.id);
				if (response.data != undefined) {
					// let componentSettingsTemplateIndex = this.config.componentSettingsTemplates.findIndex((v) => v.id === currentComponent.id);
					// if (componentSettingsTemplateIndex === -1) {
					// 	throw this.$translateWithDefault('componentSettings.edit.couldNotMatch');
					// }
					// currentComponent = this.$cs_.deepClone(this.config.componentSettingsTemplates[componentSettingsTemplateIndex]);
					currentComponent = response.data;
				} else {
					throw this.$translateWithDefault('componentSettings.edit.templateNotDefined');
				}

				let configForm = this.configForms.find((v) => v.id === 'componentSettings');
				if (configForm != undefined) {
					configForm = this.$cs_.deepClone(configForm);
					configForm.mode = 'edit';
					configForm = this.miConfigUtilsPopulateEditFormValues(currentComponent, configForm);
				}
				return {currentComponent, configForm};
			} catch (e) {
				this.$recordError({
					type: 'warning',
					error: e,
					displayToUser: true,
					errorCode: '000147'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Get the active form template and return a config form
		 * decorated by the fields it contains.
		 *
		 * @return {Object}
		 */
		async getCurrentComponentAndConfigForSiteFormFieldTemplates() {
			try {
				const formGuids = $csForm.getActiveFormGuids();
				const activeForm = this.$cs_.deepClone($csForm.getFormSettingsObject(formGuids[0]));

				let currentComponent = activeForm;
				currentComponent.componentName = 'cs-form';

				let configForm = this.configForms.find((v) => v.id === 'siteForm');
				const response = await this.miConfigUtilsGetConfigItem('SiteFormConfiguration', currentComponent.id);
				if (configForm != undefined && response.data != undefined) {
					configForm = this.$cs_.deepClone(configForm);
					configForm.mode = 'edit';
					configForm = this.miConfigUtilsPopulateEditFormValues(response.data, configForm);
				} else {
					console.error(`Unable to find either a configForm or a siteForm to edit`);
				}
				return {currentComponent, configForm};
			} catch (e) {
				this.$recordError({
					type: 'warning',
					error: e,
					displayToUser: true,
					errorCode: '000148'
				});
			}
		},
		/**
		 * @public
		 * @description
		 * Saves the completed forms config, transforming it first when required
		 *
		 * @param {Object} payload The raw form payload
		 * @param {Object} config The axios call cofiguration object
		 * @param { Function } saveProgressCallback The callback function to call when save is finished
		 */
		async onSaveConfigData(payload, config, saveProgressCallback) {
			try {
				let transformedPayload = {};

				const collectionId = payload.collectionId;

				transformedPayload = this.$cs_.deepClone(this.transformStandardPayload(payload), {filter: (v) => v != null});
				transformedPayload = this.$recursivelyRemoveNullAndEmptyAndUndefinedFromObject(transformedPayload, transformedPayload);

				let url = this.miConfigUtilsGetConfigUiCollectionFormUrl(collectionId, payload);

				const callerGuid = payload.callerGuid;
				delete payload.callerGuid;

				let payloadNewItemSettings;
				if (payload.payloadSettings) {
					payloadNewItemSettings = payload.payloadSettings;
					delete payload.payloadSettings;
				}

				let response = await this.$axios.post(url, transformedPayload, config);
				await response.data;

				if (this.configForms == undefined) {
					let configModule = await import('@/js/modules/configFormsES6');
					let requests = [configModule.default.getConfigFormsObject(), configModule.default.getConfigLookupsObject()];

					this.configForms = await requests[0];
					await requests[1]; //CONFIG LOOKUPS HAVE BEEN MOVED TO THE ES6 Module
				} else {
					let configModule = await import('@/js/modules/configFormsES6');
					await configModule.default.getConfigLookupsObject();
				}

				//SET the ConfigLookUps data object creates this.config object - in miConfigUtils
				await this.miConfigUtilsGetSiteConfig();

				const callerComponent = this.$cs_.querySelectorVue(this, {name: 'cs-select', propsKey: ['field', 'guid'], propsValue: callerGuid});
				if (callerComponent != undefined && payloadNewItemSettings != undefined && typeof callerComponent[payloadNewItemSettings.onSaveCallBackMethod] == 'function') {
					let newValue = [];
					if (this.$cs_.get(response.data, 'data', 'id')) {
						newValue.push(response.data.data.id);
					}
					callerComponent[payloadNewItemSettings.onSaveCallBackMethod]({response: response.data, newValue, callerGuid});
					if (typeof saveProgressCallback == 'function') {
						saveProgressCallback(false, callerGuid);
					}
					//if we have three forms open (standard form and the config form) re-initialise the base form
					if (this.decoratedSiteConfigForm != undefined && this.decoratedSiteConfigForm.itemId != undefined) {
						const firstFormGuidOpened = $csForm.getFirstFormGuidOpened();
						if (firstFormGuidOpened != undefined) {
							let formSettings = $csForm.getFormSettingsObject(firstFormGuidOpened);
							const parentFormComponent = this.$cs_.querySelectorVue(this, {name: 'cs-form', dataKey: 'formGuid', dataValue: firstFormGuidOpened});							

							if (parentFormComponent != undefined && formSettings.itemId != this.decoratedSiteConfigForm.itemId) {
								// we are going to reinitialise the original/base form we opened from the grid.
								parentFormComponent.initializeForm(parentFormComponent.formSettings.versionItemId);
							}
						}
					}

					this.showNotification('success', response.data.message, true, 'standard');
				} else {
					//clear and close the form on save
					this.toggleOverlay(false);
					//force a screen refresh to refresh the component data
					this.$emit('updateComponentVersion');

					this.showNotification('success', response.data.message, true, 'standard');

					//form dirty code moved to cs-form method emit
					this.overlayVisible = false;
				}
			} catch (error) {
				this.toggleOverlay(false);
				this.overlayVisible = false;
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000217'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Shows the dialog to confirm the form closure
		 * @param {function} routerNext The next function from the vue router
		 */
		showFormCloseConfirmDialog(routerNext) {
			let popoverSettings = {
				title: `${this.$translateWithDefault('forms.close')}?`,
				text: this.$translateWithDefault('forms.fieldDirtyCloseWarning'),
				buttonConfirmText: this.$translateWithDefault('forms.yesCloseForm'),
				buttonCancelText: this.$translateWithDefault('forms.keepWorking')
			};

			const sideOverlay = this.$cs_.querySelectorVue(this, {name: 'cs-sideoverlay'});

			if (sideOverlay != null) {
				// check for dual form, make sure they know they will close 2x forms
				if (this.$cs_.get(sideOverlay, 'settings', 'dualMode') === true) {
					popoverSettings = {
						title: `${this.$translateWithDefault('forms.closeForms')}?`,
						text: this.$translateWithDefault('forms.fieldDirtyCloseWarning'),
						buttonConfirmText: this.$translateWithDefault('forms.yesCloseForms'),
						buttonCancelText: this.$translateWithDefault('forms.keepWorking')
					};
				} else if (this.$cs_.get(sideOverlay, 'settings', 'isDiagram') === true) {
					// is a process map
					popoverSettings = {
						title: this.$translateWithDefault('processMaps.close.diagramTitle'),
						text: this.$translateWithDefault('processMaps.close.diagramText'),
						buttonConfirmText: this.$translateWithDefault('processMaps.closeDiagram'),
						buttonCancelText: this.$translateWithDefault('forms.keepWorking')
					};
				}
			}

			this.$showSystemPopup(popoverSettings).then((confirmed) => {
				if (confirmed) {
					$csForm.setAllFormsDirtyStatus(false);
					//close the overlay form if open
					this.overlayVisible = false;
					routerNext();
				} else {
					routerNext(false);
				}
			});
		},

		/**
		 * @public
		 * @description
		 * Transform endpoint url based on data collectionId and call API to delete those data
		 *
		 * @param { Object } payload The object that containe a collectionId name and an id of deleting data
		 *
		 * @returns void
		 */
		onDeleteConfigData(payload) {
			try {
				let dataUrl = this.$getApiBaseUrl();

				const collectionId = payload.collectionId;
				const dataId = payload.id;

				switch (collectionId) {
					case 'componentSettingsTemplates':
						dataUrl += '/api/SiteConfiguration/DeleteComponentSettings/';
						break;
				}

				// append the config id
				dataUrl += dataId;

				this.$axios
					.post(dataUrl)
					.then((response) => {
						this.showNotification('success', response.data, true, 'standard');
					})
					.finally(() => {
						this.overlayVisible = false;
					});
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000218'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Receives and opens the relevant full screen wizard component
		 *
		 * @param {String} componentName The component name to open, defaults to cs-sitebuilder
		 * @param {Object} componentData The component data to pass to the component
		 */
		openFullscreenWizard(componentName = 'cs-site-builder', componentData = {}) {
			if (componentName == 'cs-site-builder') Object.assign(componentData, {appEntities: this.entities});

			this.fullScreenWizardComponent = componentName;
			this.fullScreenWizardComponentData = componentData;
			this.fullScreenWizardVisible = true;

			const csHeader = this.$cs_.querySelectorVue(this.$root, {name: 'cs-header', method: 'toggleMenuButtonVisibility'});
			csHeader?.toggleMenuButtonVisibility(false);
		},

		/**
		 * @public
		 * @description
		 * Slides back a full width overlay container and display main app navigation menu button
		 */
		async onCloseFullScreenWizard(refresh = false) {
			const csHeader = this.$cs_.querySelectorVue(this.$root, {name: 'cs-header', method: 'toggleMenuButtonVisibility'});
			csHeader?.toggleMenuButtonVisibility(true);

			if (this.fullScreenWizardComponent === 'cs-site-builder' && refresh === true) {
				const csConfig = this.$cs_.querySelectorVue(this.$root, {name: 'cs-config', method: 'init'});
				csConfig?.init();
			}

			this.fullScreenWizardVisible = false;
			this.fullScreenWizardComponent = '';
			this.fullScreenWizardComponentData = undefined;
		}
	},

	/**
	 * @public
	 * @description
	 * Handles confirming the user wishes to change screen
	 * there is a form displaying and it has been edited
	 */
	async beforeRouteUpdate(to, from, next) {
		// are we on a dashboard? If so need to check if we've been editing and make sure the user doesnt
		// leave without knowing they have unsaved changes.
		let dashboardComponent = undefined;
		if (Array.isArray(this.screen.components) && this.screen.components.length > 0) {
			const topLevelComponent = this.screen.components[0];
			if (topLevelComponent.componentName === 'cs-reports') {
				dashboardComponent = this.$cs_.querySelectorVue(this.$root, {name: 'cs-reportsdashboard'});
			}
		}

		// if the form is showing as dirty then we can show the dialog straight away
		if ($csForm.isAllFormsDirty() === true) {
			// this is a promise
			this.showFormCloseConfirmDialog(next);
		} else if (dashboardComponent != undefined && dashboardComponent.dashboardIsInEditMode === true) {
			try {
				let title, text;
				let buttonConfirmText = this.$translateWithDefault('reports.yesCloseDashboard');
				let buttonCancelText = this.$$translateWithDefault('forms.keepWorking');
				if (dashboardComponent.isReportEditDirty === true) {
					title = this.$translateWithDefault(`reports.exiting.title`);
					text = this.$translateWithDefault(`reports.exiting.text`);
				} else {
					title = this.$translateWithDefault(`reports.exitingForm.title`);
					text = this.$translateWithDefault(`reports.exitingForm.text`);
				}
				let confirmed = await this.$showSystemPopup({title, text, buttonConfirmText, buttonCancelText});

				if (confirmed === true) {
					if (dashboardComponent.isReportEditDirty === true) {
						await dashboardComponent.confirmDashboardExitEditMode();
						next();
					} else {
						next();
					}
				}
			} catch (e) {
				return;
			}
		} else {
			this.overlayVisible = false;
			this.fullScreenWizardVisible = false;

			const csHeader = this.$cs_.querySelectorVue(this.$root, {name: 'cs-header', method: 'toggleMenuButtonVisibility'});
			csHeader?.toggleMenuButtonVisibility(true);

			next();
		}
	},

	/**
	 * @public
	 * @description
	 * Handles confirming the user wishes to leave the page if
	 * there is a form displaying and it has been edited
	 */
	beforeRouteLeave(to, from, next) {
		if ($csForm.isAllFormsDirty() === true) {
			this.showFormCloseConfirmDialog(next);
		} else {
			//close the overlay form if open
			this.overlayVisible = false;
			next();
		}
	},

	computed: {
		/**
		 * @public
		 * @description
		 * Creates a unique key for each instatiation of the form component, to avoide unneccessary re-use
		 *
		 * @returns {String} Unique Key
		 */
		/*siteConfigFormKey() {
			let key = '';

			if (this.decoratedSiteConfigForm.id) {
				key = `${this.decoratedSiteConfigForm.id}_`;
			}

			if (this.decoratedSiteConfigForm.itemId) {
				key += this.$_.snakeCase(this.decoratedSiteConfigForm.itemId);
			} else {
				key += `create_${this.$_.uniqueId()}`;
			}

			if (key.length === 0) {
				key = this.$_.uniqueId();
			}

			return key;
		}*/
	}
};
</script>
<style lang="scss">
/** @format */

.cs-screen {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: auto;

	.cs-components-container {
		.cs-loading-logo-container .cs-loading-logo::after {
			box-shadow: none;
		}
	}

	.cs-overlay-slider-wrapper {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		pointer-events: none;
	}

	.cs-screen-form {
		z-index: 99;
	}

	.cs-overlay-mask--in::after {
		content: '';
		position: fixed;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		background-color: rgba(0, 0, 0, 0.34);
		animation: mask-opacity-in 0.6s ease-in-out both;
		z-index: 99;
	}

	.cs-overlay-mask--in {
		pointer-events: none;
	}

	.cs-overlay-mask--out::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		background-color: rgba(0, 0, 0, 0.34);
		animation: mask-opacity-out 0.3s ease-in-out both;
		z-index: 99;
	}

	@keyframes mask-opacity-in {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@keyframes mask-opacity-out {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}
}

//filter styles moved here as shared between shared filters and policy manager
.cs-shared-filters {
	display: flex;
	flex-direction: column;
	flex: 0 0 auto;
	max-width: $cs-large-pad;
	transition-property: max-width;
	transition-delay: 0ms;
	position: relative;
	margin-bottom: $cs-standard-pad;

	@include cs-animation-linear-out-slow-in(300ms);

	> .cs-button-icon {
		align-self: flex-end;
		flex: 0 0 auto;
	}

	.cs-shared-filters-list {
		position: absolute;
		z-index: 3;
		width: 343px;
		max-height: 75vh;
		opacity: 1;
		display: none;
		flex-direction: column;
		top: 36px;
		right: 0;

		&.cs-shared-filters-list-enter,
		&.cs-shared-filters-list-leave-to {
			display: flex;
			opacity: 0;
		}

		&.cs-shared-filters-list-enter-active {
			display: flex;
			@include cs-animation-fast-out-slow-in(250ms);
		}

		&.cs-shared-filters-list-leave-active {
			display: flex;
			@include cs-animation-linear-out-slow-in(200ms);
			transition-delay: 0ms;
		}

		&:before {
			top: -4px;
			right: 10px;
			border: solid transparent;
			content: ' ';
			height: $cs-medium-pad;
			width: $cs-medium-pad;
			position: absolute;
			pointer-events: none;
			background-color: inherit;
			transform: rotate(45deg);
			@include cs-shadow-6dp();
		}
		h3 {
			flex: 0 0 auto;
			position: relative;
			background-color: inherit;
			margin-bottom: 0;
			padding: $cs-medium-pad $cs-standard-pad;
			border-top-left-radius: $cs-border-radius;
			border-top-right-radius: $cs-border-radius;
		}

		.cs-filters-body {
			flex: 1 1 auto;
			overflow: auto;
			padding: $cs-medium-pad $cs-standard-pad;
			border-bottom-width: $cs-micro-pad;
			border-bottom-style: solid;
			border-top-width: $cs-micro-pad;
			border-top-style: solid;
		}
	}

	.cs-filters-footer {
		display: flex;
		justify-content: space-between;
		border-top-width: 1px;
		border-top-style: solid;
		border-bottom-left-radius: $cs-border-radius;
		border-bottom-right-radius: $cs-border-radius;
		flex: 0 0 auto;
		position: relative;
		background-color: inherit;
		padding: $cs-small-pad $cs-standard-pad;
		margin-bottom: 0;
	}

	&.cs-display-mode-primary {
		border-radius: $cs-button-border-radius;
		.cs-shared-filters-list {
			position: relative;
			top: 0;
			display: flex;
			&:before {
				display: none;
			}
			.cs-filters-body {
				padding-bottom: 0px;
			}
		}
	}
	&.cs-shared-filters-open {
		@include cs-animation-fast-out-slow-in(150ms);

		.cs-shared-filters-list {
			display: flex;
		}
	}

	&.cs-popup-filters {
		.cs-shared-filters-list {
			z-index: 5;
			@include cs-shadow-6dp();
		}
	}

	@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
		&.cs-popup-filters {
			.cs-filters-body {
				overflow: visible;
			}
		}
	}
	.cs-filters-section {
		display: flex;
		flex-direction: column;
		flex: 1 1 100%;

		.cs-filters-section--header {
			margin-bottom: $cs-micro-pad;

			h4 {
				padding: $cs-micro-pad 0;
				border-bottom-width: 1px;
				border-bottom-style: solid;
			}
		}
	}

	@include media-breakpoint-down(lg) {
		&.cs-display-mode-default {
			.cs-shared-filters-list {
				@include cs-shadow-6dp();
				transition-property: opacity;
				border-radius: $cs-border-radius;
			}
		}
	}

	@include media-breakpoint-up(xl) {
		&.cs-shared-filters-auto-popout {
			overflow: hidden;
			min-width: $cs-large-pad;
			transition-delay: 250ms;

			> .cs-button-icon {
				position: absolute;
				z-index: 2;
			}

			.cs-filters-section {
				.cs-filters-section--header {
					padding: 0;
				}
			}

			.cs-shared-filters-list {
				display: flex;
				position: relative;
				z-index: 1;
				top: auto;
				right: auto;
				width: 250px;
				flex: 1 1 auto;
				height: 100%;
				max-height: none;
				overflow: hidden;
				margin-right: -16px;

				h3 {
					padding: $cs-micro-pad $cs-standard-pad;
				}

				&.cs-shared-filters-list-enter-active {
					transition-delay: 400ms;
				}

				&:before {
					display: none;
				}
			}

			&.cs-shared-filters-open {
				position: relative;
				z-index: 1;
				overflow: initial;
				transition-delay: 0ms;

				&:before {
					content: '';
					position: absolute;
					z-index: 300;
					left: -1px;
					top: 0;
					bottom: 0;
					border-left-width: 1px;
					border-left-style: solid;
					border-color: inherit;
				}
			}
			&.cs-display-mode-default.cs-shared-filters-open {
				max-width: 266px;
			}
			&.cs-shared-filters-picker-open {
				.cs-shared-filters-list {
					overflow: visible;
					z-index: 400;
				}
			}

			&.cs-shared-filters-force-popout {
				> .cs-button-icon {
					position: relative;
				}

				.cs-shared-filters-list {
					position: absolute;
				}
			}
		}
	}

	//Primary filter overrides
	&.cs-display-mode-primary {
		max-width: 100%;

		.cs-shared-filters-list {
			width: 100%;
			.cs-filters-body {
				overflow: unset;
			}
			@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
				.cs-filters-body {
					overflow: visible;
				}
			}
			.cs-filters-section--body {
				display: flex;
				justify-content: space-between;
				margin: 0 -10px;
				.cs-filter {
					flex-grow: 1;
					margin: 0 10px;
				}
			}
			.cs-primary-filter-wrapper {
				display: flex;
				flex-wrap: wrap;
				&.cs-full-width-filters {
					.cs-filter-grid-1 {
						flex: 0 0 auto;
						width: calc(100% - 20px);
					}
					.cs-filter-grid-3 {
						flex: 0 0 auto;
						width: calc(33.3333% - 20px);
					}
					.cs-filter-grid-4 {
						flex: 0 0 auto;
						width: calc(25% - 20px);
					}
					.cs-filter-grid-5 {
						flex: 0 0 auto;
						width: calc(20% - 20px);
					}
				}
				&.cs-mobile-filters {
					.cs-filter {
						flex: 0 0 auto;
						width: calc(50% - 20px);
						&:nth-child(5n) {
							flex: 0 0 auto;
							width: calc(100% - 20px);
						}
					}
					.cs-filter-grid-1,
					.cs-filter-grid-3 {
						flex: 0 0 auto;
						width: calc(100% - 20px);
					}
				}
			}
		}
	}
}
.cs-primary-filter-buttons {
	@include cs-animation-linear-out-slow-in();
	transition-property: height;
	height: 0px;
	overflow: hidden;
	&.cs-primary-filter-buttons-visible {
		height: 53px;
	}
}

.cs-fullscreen-wizard-slider {
	position: fixed;
	width: 100%;
	height: 100%;
	z-index: 100;
	margin-top: -$cs-logo-header-height;
}

.cs-fullscreen-wizard {
	display: flex;
	flex-direction: column;
	flex: 1 1 100%;
	width: 100%;
	height: 100%;
	padding-top: $cs-logo-header-height;
}

.cs-fullscreen-wizard-panel {
	position: relative;
	width: 100%;
	height: 100%;
	padding: $cs-standard-pad $cs-standard-pad 0 $cs-standard-pad;
	will-change: transition;
	transition: height 0.3s $cs-animation-curve-fast-out-slow-in;

	&.cs-fullscreen-wizard-panel-has-nav {
		max-height: 50%;
	}

	&.cs-fullscreen-wizard-panel-has-list {
		height: 60px;
		min-height: 60px;
	}

	&.cs-fullscreen-wizard-panel-nav-visible {
		height: 103px;
		min-height: 103px;
		transition: height 0.3s $cs-animation-curve-linear-out-slow-in;
		overflow: hidden;
	}

	.cs-fullscreen-wizard-panel-welcome {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		overflow: hidden;

		height: 0%;

		&.open {
			height: 100%;
		}

		.cs-fullscreen-wizard-panel-heading {
			text-align: center;
			margin-bottom: $cs-standard-pad;
		}

		.cs-fullscreen-wizard-panel-form {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;

			.cs-fullscreen-wizard-panel-form-container {
				display: flex;
				flex-direction: column;
				justify-content: center;
				width: 100%;
				max-width: 280px;

				> button {
					min-width: 120px;
					height: 40px;
					margin: $cs-standard-pad;
				}

				> input {
					min-width: 220px;
				}
			}
		}

		.cs-fullscreen-wizard-panel-footer {
			text-align: center;
			margin: $cs-standard-pad 0;

			.cs-fullscreen-wizard-panel-cancel-button {
				opacity: 0.5;
				cursor: pointer;

				&:hover {
					text-decoration: underline;
				}
			}
		}
	}

	.cs-fullscreen-wizard-panel-top {
		display: flex;
		// margin-bottom: $cs-small-pad;

		.cs-fullscreen-wizard-panel-title {
			margin-bottom: 0;
			margin-left: $cs-standard-pad;
		}
	}

	.cs-fullscreen-wizard-panel-nav {
		display: flex;
		align-items: center;
		min-height: 53px;

		.cs-fullscreen-wizard-panel-nav-wrapper {
			width: 100%;
			margin-right: $cs-button-fab-size;
			overflow-x: auto;

			&::-webkit-scrollbar {
				display: none; // Chrome, Safari, Webkit
				-ms-overflow-style: none; // IE and Edge
			}

			.cs-fullscreen-wizard-panel-nav-list {
				display: flex;
				list-style-type: none;
				padding-inline-start: 0;
				margin: 0;

				> li {
					position: relative;
					white-space: nowrap;
					padding: $cs-standard-pad 0;
					margin-right: $cs-standard-pad;

					&:first-child {
						margin-left: $cs-standard-pad;
					}

					&.cs-active::before {
						content: '';
						width: 8px;
						height: 8px;
						position: absolute;
						left: 50%;
						bottom: 0;
						background-color: white;
						border-radius: 50%;
						transform: translate(-50%, 50%);
					}
				}
			}
		}
	}

	.cs-fullscreen-wizard-save-button-container {
		position: absolute;
		// top: $cs-logo-header-height;
		top: 0;
		right: 0;

		.cs-add-entity-button {
			width: 34px;
			height: 34px;
		}

		.cs-button-icon.cs-button-fab {
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.cs-loading-spinner-container {
			margin: 0 !important;
		}
	}

	@include media-breakpoint-up(md) {
		.cs-fullscreen-wizard-panel-welcome {
			.cs-fullscreen-wizard-panel-form {
				.cs-fullscreen-wizard-panel-form-container {
					flex-direction: row;

					> button {
						margin: 0 $cs-standard-pad;
					}
				}
			}
		}
	}
}

.cs-fullscreen-wizard-form {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
}

.cs-fullscreen-wizard-body-wrapper {
	display: flex;
	flex: 1 1 auto;
	overflow: hidden;
}

@include media-breakpoint-up(lg) {
	.cs-fullscreen-wizard-form {
		flex-direction: row;

		.cs-fullscreen-wizard-form-sidebar {
			flex-direction: column;
		}

		.cs-fullscreen-wizard-form-wrapper {
			.cs-fullscreen-wizard-form-row-group {
				.cs-fullscreen-wizard-form-row {
					flex-wrap: nowrap;

					.cs-fullscreen-wizard-form-field-container {
						margin: 0 $cs-small-pad;
					}
				}
			}
		}
	}
}
</style>
